import React, { useEffect, useRef, useState } from 'react';
import { Button, Radio } from 'antd';
import SchedularHeader from '../SchedularHeader/SchedularHeader';
import ResponseMessage from '../SlotSchedulerResponseMessage/SlotSchedulerResponseMessage';
import {
  getProposalId,
  getProposeToken,
  toProposalView,
  toAcceptedTimeLabel,
  describeResolveFailure,
  describeAcceptFailure,
} from '../../Utility/proposeLink';
import './CandidateSlotProposal.scss';

// A 401 does not reveal which part of the bearer link was wrong.
const UNUSABLE_LINK = describeResolveFailure({ response: { status: 401 } });

const CandidateSlotProposal = ({ repository }) => {
  const proposalId = getProposalId();
  const token = getProposeToken();

  const [resolveState, setResolveState] = useState({ status: 'INPROGRESS', view: null, failure: null });
  const [resolveAttempt, setResolveAttempt] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [acceptState, setAcceptState] = useState({ status: 'IDLE', failure: null, acceptedLabel: null });

  const isStaleRef = useRef(false);
  // State cannot stop two clicks in the same tick from sending twice.
  const isAcceptInFlightRef = useRef(false);

  useEffect(
    () => () => {
      isStaleRef.current = true;
    },
    []
  );

  useEffect(() => {
    if (!proposalId || !token) return undefined;
    let isStale = false;
    setResolveState({ status: 'INPROGRESS', view: null, failure: null });
    repository
      .resolveSlotProposal(proposalId, token)
      .then(response => {
        if (!isStale) setResolveState({ status: 'COMPLETED', view: toProposalView(response.data), failure: null });
      })
      .catch(error => {
        if (!isStale) setResolveState({ status: 'FAILED', view: null, failure: describeResolveFailure(error) });
      });
    return () => {
      isStale = true;
    };
  }, [repository, proposalId, token, resolveAttempt]);

  const confirmSelectedSlot = () => {
    if (!selectedSlot || isAcceptInFlightRef.current) return;
    isAcceptInFlightRef.current = true;
    setAcceptState({ status: 'INPROGRESS', failure: null });
    repository
      .acceptSlotProposal(proposalId, token, selectedSlot.startUtc)
      .then(response => {
        isAcceptInFlightRef.current = false;
        // Only the server can name the time it actually took.
        const accepted = response && response.data ? response.data : {};
        const acceptedLabel = toAcceptedTimeLabel(resolveState.view, accepted.acceptedStartUtc);
        if (!isStaleRef.current) setAcceptState({ status: 'TAKEN', failure: null, acceptedLabel });
      })
      .catch(error => {
        isAcceptInFlightRef.current = false;
        if (!isStaleRef.current)
          setAcceptState({ status: 'FAILED', failure: describeAcceptFailure(error), acceptedLabel: null });
      });
  };

  const renderOutcome = ({ title, description }, children = null) => (
    <>
      <SchedularHeader showCalendar={false} />
      <div className="slot-schedular-body centered">
        <ResponseMessage title={title} description={description}>
          {children}
        </ResponseMessage>
      </div>
    </>
  );

  if (!proposalId || !token) return renderOutcome(UNUSABLE_LINK);

  // The calendar consumer has not run yet; a 200 without a time names no hour.
  if (acceptState.status === 'TAKEN') {
    const { acceptedLabel } = acceptState;
    return renderOutcome({
      title: 'That time is taken',
      description: acceptedLabel
        ? `We have taken ${acceptedLabel}. Your recruiter will send a confirmation once the interview is arranged.`
        : 'Your recruiter will send a confirmation once the interview is arranged.',
    });
  }

  if (acceptState.status === 'FAILED' && !acceptState.failure.canRetry) return renderOutcome(acceptState.failure);

  if (resolveState.status === 'INPROGRESS') {
    return (
      <div className="loader-container">
        <div className="loader" />
      </div>
    );
  }

  if (resolveState.status === 'FAILED') {
    const { failure } = resolveState;
    return renderOutcome(
      failure,
      failure.canRetry ? (
        <Button className="propose-retry" type="primary" onClick={() => setResolveAttempt(attempt => attempt + 1)}>
          Try again
        </Button>
      ) : null
    );
  }

  const { slots, candidateName, recruiterName, timeZoneLabel, expiresAtLabel } = resolveState.view;
  const perDayZone = new Set(slots.map(slot => slot.zoneLabel)).size > 1;
  const contact = recruiterName || 'your recruiter';

  // A 200 with no usable times cannot offer a booking action.
  if (!slots.length) {
    return renderOutcome({
      title: 'There are no times to choose from',
      description: 'Reply to the text message that brought you here and your recruiter will send new times.',
    });
  }

  const isAccepting = acceptState.status === 'INPROGRESS';

  return (
    <>
      <SchedularHeader showCalendar={false} />
      <div className="slot-schedular-body">
        <div className="info">
          {candidateName ? <div className="name">Hi, {candidateName}</div> : null}
          <div className="calendar-schedule-text">Choose your interview time</div>
          <div className="calendar-schedule-description">
            {contact} has offered you the times below. Pick the one that suits you and confirm it.
            {expiresAtLabel ? ` This link works until ${expiresAtLabel}.` : ''}
          </div>
          <div className="propose-timezone">
            {perDayZone ? 'Each date shows its time zone below.' : `All times are shown in ${timeZoneLabel}.`}
          </div>
        </div>
        <div className="separator" />
        <div className="propose-picker">
          <Radio.Group
            className="propose-slots"
            value={selectedSlot ? selectedSlot.startUtc : null}
            onChange={event => setSelectedSlot(slots.find(slot => slot.startUtc === event.target.value) || null)}
          >
            {slots.map(slot => (
              <Radio className="propose-slot" key={slot.startUtc} value={slot.startUtc}>
                <span className="propose-slot-label">{slot.label}{perDayZone ? ` ${slot.zoneLabel}` : ''}</span>
              </Radio>
            ))}
          </Radio.Group>
          {acceptState.status === 'FAILED' ? (
            <p className="propose-accept-failure">
              {acceptState.failure.title}. {acceptState.failure.description}
            </p>
          ) : null}
          <Button
            className="propose-confirm"
            type="primary"
            disabled={!selectedSlot || isAccepting}
            onClick={confirmSelectedSlot}
          >
            {isAccepting ? 'Confirming' : 'Confirm this time'}
          </Button>
        </div>
      </div>
    </>
  );
};

export default CandidateSlotProposal;
